import { COLORS } from "@coeff/utils";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Typography } from "../../../common";
import { FlexRow } from "../../../common";
import { Button } from "../../../common/Button";
import { Card } from "../../../common/Card";
import { Modal } from "../../../common/Modal";
import { Radio } from "../../../common/Radio";
import { useEventManagerContext } from "../../../globals/contexts";
import { useAuthContext } from "../../context/AuthContext";

import { HAS_DETAILS_QUESTIONS, IS_ADMIN_QUESIONS } from "./constants";

const StyledRadio = styled(Radio)<{
  noMargin?: boolean;
  noPadding?: boolean;
}>`
  display: flex;
  height: auto;
  font-size: 14px;
  color: ${COLORS.black85};
  white-space: normal;
  background-color: ${COLORS.white};
  border-radius: 4px;
  margin-bottom: ${({ noMargin }) => (noMargin ? 0 : "10px")};
  padding: ${({ noPadding }) => (noPadding ? 0 : "10px")};

  span {
    &.ant-radio {
      height: 16px;
      position: relative;
      margin-right: 5px;

      .ant-radio-checked {
        &::after {
          animation-duration: 0s !important;
        }
      }
    }
  }
`;

const StyledModel = styled(Modal)`
  max-width: calc(100vw - 32px);
`;

type AskDetailsState = {
  isAdmin: boolean | null;
  hasCredentials: boolean | null;
};

export const AskDetailsStep: React.FC<{
  onStepChange: (values: AskDetailsState) => void;
}> = ({ onStepChange }) => {
  const { analyticsContext } = useAuthContext();
  const fromConnectedSource = analyticsContext?.fromConnectedSource;
  const fromOnboarding = analyticsContext?.fromOnboarding;

  const { track } = useEventManagerContext();

  const [values, setValues] = useState<AskDetailsState>({
    isAdmin: null,
    hasCredentials: null,
  });

  const [showModel, setShowModel] = useState(false);

  const unTouchedAdmin = values.isAdmin === null;
  const unTouchedCredential = values.hasCredentials === null;

  useEffect(() => {
    track("conn_create_viewed", {
      import_data_source: "looker",
      event_from: fromConnectedSource
        ? "add_connection"
        : fromOnboarding
        ? "app_onboarding"
        : "add_import",
      looker_version: 2,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StyledModel visible={showModel} onCancel={() => setShowModel(false)}>
        <Typography fontWeight="bold" variant="h6">
          Do you have the Looker Client ID & Secret?
        </Typography>
        <br />

        {HAS_DETAILS_QUESTIONS.map(option => (
          <StyledRadio
            noMargin
            noPadding
            key={option.title}
            value={option.value}
            checked={unTouchedCredential ? false : values.hasCredentials === option.value}
            onChange={event =>
              setValues(values => ({ ...values, hasCredentials: event.target.value }))
            }
          >
            <Typography fontWeight={700} fontSize={14} lineHeight={"17px"}>
              {option.title}
            </Typography>
            <Typography
              color="textTertiary"
              fontSize={14}
              lineHeight={"16px"}
              style={{ marginTop: 6, color: COLORS.black50 }}
            >
              {option.description}
            </Typography>
            <br />
          </StyledRadio>
        ))}

        <FlexRow style={{ marginTop: "16px", padding: "0 30px" }}>
          <Button
            type="primary"
            disabled={values.hasCredentials === null}
            onClick={() => {
              onStepChange(values);
              setShowModel(false);
            }}
            style={{ width: "100%" }}
          >
            Next
          </Button>
        </FlexRow>
      </StyledModel>

      <Card style={{ padding: "20px 10px", margin: "15px", background: COLORS.coeblue11 }}>
        <Typography fontWeight={700} color="textPrimary" align="center">
          Are you a Looker admin?
        </Typography>
        <Typography
          color="textTertiary"
          align="center"
          style={{ padding: "0 24px", marginTop: 10 }}
        >
          Connecting to Looker requires some information that only admins can access.
        </Typography>
        <div style={{ margin: "15px 0" }}>
          {IS_ADMIN_QUESIONS.map(option => (
            <StyledRadio
              key={option.title}
              value={option.value}
              checked={unTouchedAdmin ? false : values.isAdmin === option.value}
              onChange={event => setValues(values => ({ ...values, isAdmin: event.target.value }))}
            >
              <Typography fontWeight={700} fontSize={14} lineHeight={"17px"}>
                {option.title}
              </Typography>
              <Typography
                color="textTertiary"
                fontSize={14}
                lineHeight={"16px"}
                style={{ marginTop: 6, color: COLORS.black50 }}
              >
                {option.description}
              </Typography>
            </StyledRadio>
          ))}
        </div>

        <FlexRow style={{ marginTop: "20px", padding: "0 30px" }}>
          <Button
            type="primary"
            disabled={values.isAdmin === null}
            onClick={() => {
              if (values.isAdmin) {
                onStepChange(values);
                return;
              }
              setShowModel(true);
            }}
            style={{ width: "100%" }}
          >
            Next
          </Button>
        </FlexRow>
      </Card>
    </>
  );
};
